/* eslint-disable ember/no-runloop */
import { set } from '@ember/object';
import { begin, end } from '@ember/runloop';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import { PRISM_SPACING_400 } from '@mylighthouse/prism-foundation';
import {
  getWeekendPlotBands,
  tickPositionerDayWeek,
} from 'lumen/utils/charts/chart-utils';
import { MI_OVERVIEW_COLUMN_CONFIG } from 'lumen/utils/constants/market-insight-constants';
import { xAxisLabelFormatterForOverview } from 'lumen/utils/market-insight/market-insight-helpers';

interface HotelMarketInsightChartsBaseChartOtbBaseSignature {
  Args: {};
}

export default class HotelMarketInsightChartsBaseChartOtbBase extends Component<HotelMarketInsightChartsBaseChartOtbBaseSignature> {
  @service declare access: Services['access'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  // eslint-disable-next-line ember/no-unused-services
  @service declare intl: Services['intl'];
  // eslint-disable-next-line ember/no-unused-services
  @service declare miSelections: Services['mi-selections'];
  // eslint-disable-next-line ember/no-unused-services
  @service declare usageAnalytics: Services['usage-analytics'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartOptions() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;
    const chartOptions = {
      chart: {
        height: 250,
        marginLeft: 80,
        marginTop: 40,
        zoomType: null,
        spacingBottom: 0,
      },
      xAxis: {
        type: 'datetime',
        lineColor: 'var(--prism-color-border-neutral-default)',
        crosshair: true,
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        tickPositioner() {
          return tickPositionerDayWeek(
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            this.dataMin,
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            this.dataMax,
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            that.args.isDailyOverview,
          );
        },
        labels: {
          // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
          formatter() {
            return xAxisLabelFormatterForOverview(
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
              this.value,
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
              that.args.isDailyOverview,
              that.intl,
            );
          },
        },
        plotBands: this.weekendPlotBands,
      },
      yAxis: {
        title: {
          text: '',
        },
        labels: {
          // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
          formatter() {
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            return that.intl.formatNumber(this.value, { style: 'percent' });
          },
        },
      },
      tooltip: {
        hideDelay: 0.2,
        padding: 0,
        borderWidth: 0,
        shadow: false,
        shared: true,
        shape: 'square',
        distance: 20,

        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        formatter() {
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          const { day } = this.points[0].point;
          // Depending on the aggregation the user is looking at, we'll need to look at daily or weekly summaries for our missing data check
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          const hasDataForDay =
            // @ts-expect-error: Incorrect type
            that.args.isDailyOverview &&
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            !day.mostRecentDailySummary?.isDestinationDemandIndexMissing;
          const hasDataForWeek =
            // @ts-expect-error: Incorrect type
            !that.args.isDailyOverview &&
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            !day.mostRecentWeeklySummary?.isDestinationDemandIndexMissing;

          if (hasDataForDay || hasDataForWeek) {
            // Necessary to get correct data for tooltips, needs to be in separate runloop
            begin();
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const occupancy = that.args.isDailyOverview
              ? // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                day.occupancy
              : // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                day.weeklyOccupancy;

            set(that, 'tooltipOccupancy', occupancy);

            // @ts-expect-error: Incorrect type
            if (that.occupancyChangeExtractDate) {
              set(
                that,
                'tooltipOccupancyChange',
                // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
                occupancy?.occupancyChangeForExtractDate(
                  // @ts-expect-error: Incorrect type
                  that.occupancyChangeExtractDate,
                ),
              );
            }
            if (that.access.hasShortTermRentalAccess) {
              set(
                that,
                'tooltipShortTermRentalOccupancy',
                // @ts-expect-error: Incorrect type
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                that.shortTermRentalMap[day.formattedDate],
              );
            }
            end();
            // @ts-expect-error: Incorrect type
            return document.getElementById('dummy-tooltip-for-market-otb')
              .innerHTML;
          }

          // We don't want to show the MI day tooltip in case of missing market data. Show the same no data explanation message as in the calendar
          set(that, 'tooltipOccupancy', null);
          set(that, 'tooltipOccupancyChange', null);
          // Use Ember-tooltip default background color
          return `<div class="info-tooltip" style="padding:6px 10px; color: var(--prism-color-text-inverse); background: var(--prism-color-background-inverse);">${that.intl.t(
            'market_insight.calendar_tooltip.no_data_message',
          )}</div>`;
        },
        useHTML: true,
      },
      credits: {
        enabled: false,
      },
      legend: {
        enabled:
          this.access.hasFeature('otb.market_benchmark') ||
          this.access.hasShortTermRentalAccess,
        itemMarginTop: PRISM_SPACING_400,
        margin: 0,
        padding: 0,
        symbolWidth: 8,
        symbolHeight: 8,
        itemStyle: {
          color: 'var(--prism-color-text-neutral-subdued)',
        },
        itemHoverStyle: {
          color: 'var(--prism-color-interactive-text-secondary-hover)',
        },
      },
      plotOptions: {
        column: MI_OVERVIEW_COLUMN_CONFIG,
        series: {
          // @ts-expect-error: Incorrect type
          cursor: that.args.onChartClicked ? 'pointer' : null,
          events: {
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
            click(e) {
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-call
              that.args.onChartClicked?.(e);

              if (that.miSelections.isDailyOverview) {
                const dateClicked = that.hotelAndMarketService.toLocalTime(
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
                  e.point.x,
                );

                that.usageAnalytics.track('Daily Market Demand Viewed', {
                  eventTrigger: 'occupancy-chart',
                  daysOffsetFromToday: dateClicked
                    .startOf('day')
                    .diff(
                      that.hotelAndMarketService.getLocalTime().startOf('day'),
                      'days',
                    ),
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                  isOutlier: e.point.day.outliers.length > 0,
                });
              }
            },
          },
        },
      },
    };

    return chartOptions;
  }

  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get shortTermRentalMap() {
    const shortTermRentalMap = {};
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    this.args.sortedDays.forEach((day) => {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      shortTermRentalMap[day.formattedDate] = (day.rentalOccupancies ?? [])[0];
    });
    return shortTermRentalMap;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get weekendPlotBands() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (this.args.isDailyOverview && this.args.sortedDays.length) {
      // @ts-expect-error: Incorrect type
      const { sortedDays } = this.args;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const startDate = sortedDays[0].date;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const endDate = sortedDays[sortedDays.length - 1].date;

      return getWeekendPlotBands(
        this.hotelAndMarketService,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        startDate,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        endDate,
      );
    }

    return [];
  }
}
